import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatError, setAuthToken } from "../lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null=checking, false=guest, obj=user
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    // Cross-device sync: when the user switches back to this tab (e.g.
    // after buying Pro on their phone), silently re-fetch /auth/me so
    // the subscription state updates without requiring a page reload.
    // Only re-fetches when the user is already logged in — no wasted
    // calls for guests.
    useEffect(() => {
        const onFocus = () => {
            if (document.visibilityState === "visible") {
                // Best-effort background refresh — never toggle `loading`
                // (which would flash the whole app skeleton on every
                // tab-switch) and never overwrite `user` with `false`
                // on transient network errors.
                api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => {});
            }
        };
        document.addEventListener("visibilitychange", onFocus);
        window.addEventListener("focus", onFocus);
        return () => {
            document.removeEventListener("visibilitychange", onFocus);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    const _storeAuth = (data) => {
        // Backend now returns `token` alongside the user object so the
        // frontend can persist it locally — cookies are unreliable when
        // frontend & backend live on different origins (third-party-cookie
        // blocking on Safari / Brave / strict Firefox).
        if (data?.token) setAuthToken(data.token);
        setUser(data);
    };

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            _storeAuth(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatError(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            _storeAuth(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = async () => {
        // Always clear local state, even if the server call fails (offline,
        // 502, expired token). Prevents an uncaught error from leaving the
        // user stuck on the page.
        try {
            await api.post("/auth/logout");
        } catch (e) {
            // swallow — local state below is the source of truth for the UI
            // eslint-disable-next-line no-console
            console.warn("Logout API call failed (clearing client state anyway):", e?.message);
        }
        setAuthToken(null);
        setUser(false);
    };

    const updateMe = async (patch) => {
        const { data } = await api.patch("/users/me", patch);
        setUser(data);
        return data;
    };

    const deleteAccount = async () => {
        await api.delete("/users/me");
        setAuthToken(null);
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, updateMe, deleteAccount, refresh }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
